import { supabase } from './supabase';
import { CryptoService } from './crypto';
import { FileValidationService } from './fileValidation';
import { RetryUtils, RetryableError, NonRetryableError } from './retryUtils';
import { ProgressCallback } from './progressTracker';

export interface UploadSession {
  id: string;
  user_id: string;
  file_name: string;
  file_size: number;
  file_type: string;
  category: 'image' | 'video' | 'encrypted';
  chunk_size: number;
  total_chunks: number;
  uploaded_chunks: number;
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  backblaze_file_id?: string;
  final_url?: string;
  error_message?: string;
  retry_count: number;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

export interface UploadChunk {
  id: string;
  session_id: string;
  chunk_index: number;
  chunk_size: number;
  status: 'pending' | 'uploading' | 'completed' | 'failed';
  sha256_hash?: string;
  upload_url?: string;
  part_number?: number;
  retry_count: number;
  error_message?: string;
  metadata?: Record<string, any>;
  created_at: string;
  uploaded_at?: string;
}

export interface UploadOptions {
  category: 'image' | 'video' | 'encrypted';
  encrypt?: boolean;
  chunkSize?: number;
  onProgress?: ProgressCallback;
  onChunkComplete?: (chunkIndex: number, totalChunks: number) => void;
  onError?: (error: Error) => void;
}

export class UploadError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly details?: any
  ) {
    super(message);
    this.name = 'UploadError';
  }
}

export class UploadManager {
  private static readonly DEFAULT_CHUNK_SIZE = 5 * 1024 * 1024;
  private static readonly MAX_CONCURRENT_CHUNKS = 3;
  private static readonly UPLOAD_TIMEOUT_MS = 120000;

  static async startUpload(
    file: File,
    options: UploadOptions
  ): Promise<{ sessionId: string; url: string }> {
    try {
      console.log(`[UploadManager] Starting upload for file: ${file.name}`);

      const validationResult = await FileValidationService.validateFile(
        file,
        options.category
      );

      if (!validationResult.isValid) {
        throw new UploadError(
          validationResult.error || 'Validation failed',
          'VALIDATION_ERROR',
          { file: file.name, category: options.category }
        );
      }

      if (validationResult.warnings && validationResult.warnings.length > 0) {
        console.warn('[UploadManager] Validation warnings:', validationResult.warnings);
      }

      const { data: user } = await supabase.auth.getUser();
      if (!user.user) {
        throw new UploadError('User not authenticated', 'AUTH_ERROR');
      }

      const chunkSize = options.chunkSize || this.DEFAULT_CHUNK_SIZE;
      const totalChunks = Math.ceil(file.size / chunkSize);

      console.log(
        `[UploadManager] File will be split into ${totalChunks} chunks of ${chunkSize} bytes`
      );

      const session = await this.createUploadSession(
        user.user.id,
        file,
        options.category,
        chunkSize,
        totalChunks
      );

      console.log(`[UploadManager] Created upload session: ${session.id}`);

      const chunks = await this.prepareChunks(file, session, chunkSize, options.encrypt);

      console.log(`[UploadManager] Prepared ${chunks.length} chunks`);

      const finalUrl = await this.uploadChunks(session, chunks, file, options);

      await this.completeUploadSession(session.id, finalUrl);

      console.log(`[UploadManager] Upload completed successfully: ${finalUrl}`);

      return { sessionId: session.id, url: finalUrl };
    } catch (error) {
      console.error('[UploadManager] Upload failed:', error);

      if (error instanceof UploadError) {
        throw error;
      }

      throw new UploadError(
        error instanceof Error ? error.message : 'Unknown upload error',
        'UPLOAD_ERROR',
        { error }
      );
    }
  }

  private static async createUploadSession(
    userId: string,
    file: File,
    category: 'image' | 'video' | 'encrypted',
    chunkSize: number,
    totalChunks: number
  ): Promise<UploadSession> {
    const { data, error } = await supabase
      .from('upload_sessions')
      .insert({
        user_id: userId,
        file_name: file.name,
        file_size: file.size,
        file_type: file.type,
        category,
        chunk_size: chunkSize,
        total_chunks: totalChunks,
        status: 'pending',
      })
      .select()
      .single();

    if (error || !data) {
      throw new UploadError(
        'Failed to create upload session',
        'SESSION_CREATE_ERROR',
        { error }
      );
    }

    return data as UploadSession;
  }

  private static async prepareChunks(
    file: File,
    session: UploadSession,
    chunkSize: number,
    encrypt?: boolean
  ): Promise<UploadChunk[]> {
    const chunks: Omit<UploadChunk, 'id' | 'created_at' | 'uploaded_at'>[] = [];

    for (let i = 0; i < session.total_chunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const actualChunkSize = end - start;

      const blob = file.slice(start, end);
      const buffer = await blob.arrayBuffer();

      let hash: string | undefined;
      if (encrypt) {
        hash = await CryptoService.calculateSHA256(buffer);
      }

      chunks.push({
        session_id: session.id,
        chunk_index: i,
        chunk_size: actualChunkSize,
        status: 'pending',
        sha256_hash: hash,
        retry_count: 0,
      });
    }

    const { data, error } = await supabase
      .from('upload_chunks')
      .insert(chunks)
      .select();

    if (error || !data) {
      throw new UploadError(
        'Failed to create chunk records',
        'CHUNK_CREATE_ERROR',
        { error }
      );
    }

    return data as UploadChunk[];
  }

  private static async uploadChunks(
    session: UploadSession,
    chunks: UploadChunk[],
    file: File,
    options: UploadOptions
  ): Promise<string> {
    await this.updateSessionStatus(session.id, 'in_progress');

    const { data: user } = await supabase.auth.getUser();
    if (!user.user) {
      throw new UploadError('User not authenticated', 'AUTH_ERROR');
    }

    const uploadedChunks: ArrayBuffer[] = [];
    let completedChunks = 0;

    for (let i = 0; i < chunks.length; i += this.MAX_CONCURRENT_CHUNKS) {
      const batch = chunks.slice(i, i + this.MAX_CONCURRENT_CHUNKS);

      const batchResults = await Promise.allSettled(
        batch.map((chunk) =>
          this.uploadSingleChunk(chunk, file, session, options.encrypt)
        )
      );

      for (let j = 0; j < batchResults.length; j++) {
        const result = batchResults[j];
        const chunkIndex = i + j;

        if (result.status === 'fulfilled') {
          uploadedChunks[chunkIndex] = result.value;
          completedChunks++;

          await this.updateChunkStatus(chunks[chunkIndex].id, 'completed');

          if (options.onProgress) {
            const progress = Math.round((completedChunks / chunks.length) * 100);
            options.onProgress(progress);
          }

          if (options.onChunkComplete) {
            options.onChunkComplete(chunkIndex + 1, chunks.length);
          }
        } else {
          console.error(
            `[UploadManager] Chunk ${chunkIndex} failed:`,
            result.reason
          );

          await this.updateChunkStatus(
            chunks[chunkIndex].id,
            'failed',
            result.reason?.message
          );

          throw new UploadError(
            `Failed to upload chunk ${chunkIndex}`,
            'CHUNK_UPLOAD_ERROR',
            { chunkIndex, error: result.reason }
          );
        }
      }

      await this.updateSessionUploadedChunks(session.id, completedChunks);
    }

    const combinedBuffer = this.combineChunks(uploadedChunks);

    const fileName = `${user.user.id}/${StorageService.generateFileName(
      this.getFileExtension(file.name)
    )}`;

    const finalUrl = await this.uploadToBackblaze(
      combinedBuffer,
      fileName,
      file.type
    );

    if (options.encrypt && session.category !== 'image') {
      await this.saveChunkMetadata(chunks, fileName, user.user.id);
    }

    return finalUrl;
  }

  private static async uploadSingleChunk(
    chunk: UploadChunk,
    file: File,
    session: UploadSession,
    encrypt?: boolean
  ): Promise<ArrayBuffer> {
    return RetryUtils.withRetry(
      async () => {
        try {
          await this.updateChunkStatus(chunk.id, 'uploading');

          const start = chunk.chunk_index * session.chunk_size;
          const end = Math.min(start + session.chunk_size, file.size);

          const blob = file.slice(start, end);
          const buffer = await blob.arrayBuffer();

          if (encrypt) {
            const { encrypted, iv } = await CryptoService.encryptChunk(buffer);

            await supabase
              .from('upload_chunks')
              .update({
                sha256_hash: await CryptoService.calculateSHA256(encrypted),
                metadata: { iv },
              })
              .eq('id', chunk.id);

            return encrypted;
          }

          return buffer;
        } catch (error) {
          console.error(
            `[UploadManager] Error uploading chunk ${chunk.chunk_index}:`,
            error
          );

          if (
            error instanceof Error &&
            (error.message.includes('auth') || error.message.includes('401'))
          ) {
            throw new NonRetryableError('Authentication failed', error);
          }

          throw new RetryableError(
            `Failed to process chunk ${chunk.chunk_index}`,
            error instanceof Error ? error : undefined
          );
        }
      },
      {
        maxAttempts: 3,
        onRetry: async (attempt, error) => {
          console.log(
            `[UploadManager] Retrying chunk ${chunk.chunk_index}, attempt ${attempt}:`,
            error.message
          );

          await supabase
            .from('upload_chunks')
            .update({ retry_count: attempt })
            .eq('id', chunk.id);
        },
      }
    );
  }

  private static async uploadToBackblaze(
    buffer: ArrayBuffer,
    fileName: string,
    contentType: string
  ): Promise<string> {
    return RetryUtils.withTimeout(
      async () => {
        const base64Data = this.arrayBufferToBase64(buffer);

        const uploadUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/upload-to-backblaze`;

        const response = await fetch(uploadUrl, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            fileName,
            fileData: base64Data,
            contentType,
            isBase64: true,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new RetryableError(
            `Backblaze upload failed: ${response.status} - ${errorText}`
          );
        }

        const result = await response.json();

        if (!result.url) {
          throw new UploadError(
            'No URL returned from upload',
            'UPLOAD_RESPONSE_ERROR'
          );
        }

        return result.url;
      },
      this.UPLOAD_TIMEOUT_MS,
      'Upload to Backblaze timed out'
    );
  }

  private static combineChunks(chunks: ArrayBuffer[]): ArrayBuffer {
    const totalSize = chunks.reduce((sum, chunk) => sum + chunk.byteLength, 0);
    const combined = new Uint8Array(totalSize);

    let offset = 0;
    for (const chunk of chunks) {
      combined.set(new Uint8Array(chunk), offset);
      offset += chunk.byteLength;
    }

    return combined.buffer;
  }

  private static arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 32768;
    let binary = '';

    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length));
      binary += String.fromCharCode.apply(null, Array.from(chunk));
    }

    return btoa(binary);
  }

  private static async saveChunkMetadata(
    chunks: UploadChunk[],
    filePath: string,
    userId: string
  ): Promise<void> {
    const metadata = chunks.map((chunk, index) => ({
      file_path: filePath,
      chunk_index: index,
      iv: chunk.metadata?.iv || '',
      sha256_hash: chunk.sha256_hash || '',
      encrypted_size: chunk.chunk_size,
      user_id: userId,
    }));

    const { error } = await supabase.from('file_chunks').insert(metadata);

    if (error) {
      console.error('[UploadManager] Failed to save chunk metadata:', error);
    }
  }

  private static async updateSessionStatus(
    sessionId: string,
    status: UploadSession['status'],
    errorMessage?: string
  ): Promise<void> {
    const updates: any = { status };

    if (errorMessage) {
      updates.error_message = errorMessage;
    }

    if (status === 'completed') {
      updates.completed_at = new Date().toISOString();
    }

    await supabase.from('upload_sessions').update(updates).eq('id', sessionId);
  }

  private static async updateSessionUploadedChunks(
    sessionId: string,
    uploadedChunks: number
  ): Promise<void> {
    await supabase
      .from('upload_sessions')
      .update({ uploaded_chunks: uploadedChunks })
      .eq('id', sessionId);
  }

  private static async updateChunkStatus(
    chunkId: string,
    status: UploadChunk['status'],
    errorMessage?: string
  ): Promise<void> {
    const updates: any = { status };

    if (errorMessage) {
      updates.error_message = errorMessage;
    }

    if (status === 'completed') {
      updates.uploaded_at = new Date().toISOString();
    }

    await supabase.from('upload_chunks').update(updates).eq('id', chunkId);
  }

  private static async completeUploadSession(
    sessionId: string,
    finalUrl: string
  ): Promise<void> {
    await this.updateSessionStatus(sessionId, 'completed');

    await supabase
      .from('upload_sessions')
      .update({
        final_url: finalUrl,
        completed_at: new Date().toISOString(),
      })
      .eq('id', sessionId);
  }

  private static getFileExtension(fileName: string): string {
    const lastDot = fileName.lastIndexOf('.');
    return lastDot === -1 ? '' : fileName.substring(lastDot + 1);
  }

  static async resumeUpload(
    sessionId: string,
    file: File,
    options: UploadOptions
  ): Promise<{ sessionId: string; url: string }> {
    const { data: session } = await supabase
      .from('upload_sessions')
      .select('*')
      .eq('id', sessionId)
      .single();

    if (!session) {
      throw new UploadError('Upload session not found', 'SESSION_NOT_FOUND');
    }

    if (session.status === 'completed') {
      return { sessionId: session.id, url: session.final_url! };
    }

    const { data: chunks } = await supabase
      .from('upload_chunks')
      .select('*')
      .eq('session_id', sessionId)
      .order('chunk_index');

    if (!chunks || chunks.length === 0) {
      throw new UploadError('No chunks found for session', 'CHUNKS_NOT_FOUND');
    }

    const pendingChunks = chunks.filter((c) => c.status !== 'completed');

    console.log(
      `[UploadManager] Resuming upload: ${pendingChunks.length}/${chunks.length} chunks remaining`
    );

    const finalUrl = await this.uploadChunks(
      session as UploadSession,
      pendingChunks as UploadChunk[],
      file,
      options
    );

    await this.completeUploadSession(sessionId, finalUrl);

    return { sessionId, url: finalUrl };
  }

  static async cancelUpload(sessionId: string): Promise<void> {
    await this.updateSessionStatus(sessionId, 'cancelled');

    console.log(`[UploadManager] Upload cancelled: ${sessionId}`);
  }

  static async getUploadProgress(sessionId: string): Promise<{
    uploadedChunks: number;
    totalChunks: number;
    percentage: number;
    status: string;
  }> {
    const { data: session } = await supabase
      .from('upload_sessions')
      .select('uploaded_chunks, total_chunks, status')
      .eq('id', sessionId)
      .single();

    if (!session) {
      throw new UploadError('Upload session not found', 'SESSION_NOT_FOUND');
    }

    const percentage =
      session.total_chunks > 0
        ? Math.round((session.uploaded_chunks / session.total_chunks) * 100)
        : 0;

    return {
      uploadedChunks: session.uploaded_chunks,
      totalChunks: session.total_chunks,
      percentage,
      status: session.status,
    };
  }
}

class StorageService {
  static generateFileName(extension: string): string {
    return `${Date.now()}-${Math.random().toString(36).substring(7)}.${extension}`;
  }
}
